import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../../firebase';
import { Box, Typography, CircularProgress, Button, Divider } from '@mui/material';
import { CheckCircle, Cancel, Store } from '@mui/icons-material';
import { colors, typography, radius } from '../../theme';

const ADMIN_UID = 'PjtRHS9USEfyV0jk1jujVjq6KL62';

const PLANS = [
    { key:'free',   label:'Free',   price:'RM 0',      color: colors.textFaintest, borderColor: colors.border,          bgColor: colors.bg,      maxBarbers:1,   features:['1 barber','Basic queue'] },
    { key:'basic',  label:'Basic',  price:'RM 49/mo',  color:colors.teal,            borderColor:colors.tealBorder,               bgColor:colors.tealBg,        maxBarbers:3,   features:['Up to 3 barbers','Basic queue'] },
    { key:'pro',    label:'Pro',    price:'RM 99/mo',  color: colors.primary,      borderColor: colors.primaryBorder,   bgColor: colors.bgCard,   maxBarbers:999, features:['Unlimited barbers','Analytics'] },
    { key:'master', label:'Master', price:'RM 150/mo', color:colors.planMaster,            borderColor:colors.planMasterBorder,             bgColor:colors.planMasterBg,         maxBarbers:999, features:['Unlimited barbers','Analytics','AI suggestions'], future:true },
];

const DURATIONS = [
    { label:'1 Month',  months:1  },
    { label:'3 Months', months:3  },
    { label:'6 Months', months:6  },
    { label:'1 Year',   months:12 },
];

const STATUS_STYLE = {
    pending:  { bg: colors.warningBg,  border: colors.warningBorder,  color: colors.primary, label:'Pending'  },
    approved: { bg: colors.successBg,  border: colors.successBorder,  color: colors.success, label:'Approved' },
    rejected: { bg: colors.errorBg,    border: colors.errorBorder,    color: colors.error,   label:'Rejected' },
};

function timeAgo(ts) {
    if (!ts) return '—';
    const date = ts?.toDate ? ts.toDate() : new Date(ts);
    const diff = Date.now() - date.getTime();
    const days  = Math.floor(diff / 86400000);
    const hours = Math.floor(diff / 3600000);
    const mins  = Math.floor(diff / 60000);
    if (days > 0)  return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (mins > 0)  return `${mins}m ago`;
    return 'just now';
}

function getPaymentInfo(shop) {
    const plan = shop.plan ?? 'free';
    if (plan === 'free') return { status:'free', label:'— Free Plan', color: colors.textFaintest, border: colors.border, bg: colors.bg, daysLeft:null };
    const paidUntil = shop.paidUntil?.toDate?.() ?? (shop.paidUntil ? new Date(shop.paidUntil) : null);
    if (!paidUntil) return { status:'unpaid',  label:'⏳ Unpaid',                         color: colors.primary, border: colors.warningBorder, bg: colors.warningBg, daysLeft:null };
    const daysLeft = Math.ceil((paidUntil.getTime() - Date.now()) / 86400000);
    if (daysLeft < 0)  return { status:'overdue',  label:`🚨 Overdue ${Math.abs(daysLeft)}d`, color: colors.error,   border: colors.errorBorder,   bg: colors.errorBg,   daysLeft, paidUntil };
    if (daysLeft <= 7) return { status:'expiring', label:`⚠️ Expires in ${daysLeft}d`,         color: colors.warning, border: colors.warningBorder, bg: colors.warningBg, daysLeft, paidUntil };
    return                   { status:'paid',      label:`💰 Paid · ${daysLeft}d left`,         color: colors.success, border: colors.successBorder, bg: colors.successBg, daysLeft, paidUntil };
}

function addMonths(date, n) { const d = new Date(date); d.setMonth(d.getMonth() + n); return d; }

// ─── Plan Selector ────────────────────────────────────────────────────────────
const PlanSelector = ({ current, onSelect, saving }) => (
    <Box sx={{ display:'flex', gap:1, flexWrap:'wrap', mt:1 }}>
        {PLANS.map(plan => {
            const active = current === plan.key;
            return (
                <Box key={plan.key} onClick={() => !saving && !plan.future && onSelect(plan.key)}
                    sx={{ px:1.5, py:1, borderRadius: radius.md, cursor: plan.future ? 'not-allowed' : 'pointer', bgcolor: active ? plan.bgColor : colors.bg, border:`1px solid ${active ? plan.borderColor : colors.border}`, opacity: plan.future ? 0.5 : 1, transition:'all .15s', '&:hover': !plan.future ? { borderColor: plan.borderColor } : {}, position:'relative' }}>
                    {active && <Box sx={{ position:'absolute', top:-4, right:-4, width:8, height:8, borderRadius:'50%', bgcolor: plan.color }} />}
                    <Typography sx={{ fontSize:12, fontWeight:700, color: active ? plan.color : colors.textMuted }}>{plan.label}</Typography>
                    <Typography sx={{ fontSize:10, color: colors.textFaintest }}>{plan.price}</Typography>
                    {plan.future && <Typography sx={{ fontSize:9, color: colors.textFaintest }}>Soon</Typography>}
                </Box>
            );
        })}
    </Box>
);

// ─── Shop Card ────────────────────────────────────────────────────────────────
const ShopCard = ({ shop, onUpdate, onDelete }) => {
    const [saving,         setSaving]         = useState(false);
    const [expanded,       setExpanded]       = useState(false);
    const [confirmDelete,  setConfirmDelete]  = useState(false);
    const [deleting,       setDeleting]       = useState(false);
    const [showPayModal,   setShowPayModal]   = useState(false);
    const [selectedMonths, setSelectedMonths] = useState(1);

    const status   = shop.status ?? (shop.isActive ? 'approved' : 'pending');
    const plan     = shop.plan ?? 'free';
    const ss       = STATUS_STYLE[status] ?? STATUS_STYLE.pending;
    const planInfo = PLANS.find(p => p.key === plan);
    const pi       = getPaymentInfo(shop);

    const handleStatus     = async (s) => { setSaving(true); await onUpdate(shop.id, { status: s, isActive: s === 'approved' }); setSaving(false); };
    const handlePlan       = async (p) => { setSaving(true); await onUpdate(shop.id, { plan: p, ...(p === 'free' ? { paidUntil: null } : {}) }); setSaving(false); };
    const handleMarkUnpaid = async ()  => { setSaving(true); await onUpdate(shop.id, { paidUntil: null, paymentStatus:'unpaid' }); setSaving(false); };
    const handleMarkPaid   = async ()  => {
        setSaving(true);
        const base = (pi.daysLeft > 0 && pi.paidUntil) ? pi.paidUntil : new Date();
        await onUpdate(shop.id, { paidUntil: Timestamp.fromDate(addMonths(base, selectedMonths)), paymentStatus:'paid', lastPaidAt: Timestamp.fromDate(new Date()), lastPaidMonths: selectedMonths });
        setSaving(false); setShowPayModal(false);
    };
    const handleDelete = async () => { setDeleting(true); await onDelete(shop.id); setDeleting(false); setConfirmDelete(false); };
    const paidUntilLabel = pi.paidUntil ? pi.paidUntil.toLocaleDateString('en-MY', { day:'numeric', month:'short', year:'numeric' }) : null;

    return (
        <Box sx={{ bgcolor: colors.bgCard, border:`1px solid ${ss.border}`, borderRadius: radius.lg, overflow:'hidden', mb:2 }}>

            {/* Header */}
            <Box sx={{ px:2, py:1.5, display:'flex', alignItems:'center', gap:1.5, cursor:'pointer' }} onClick={() => setExpanded(e => !e)}>
                <Box sx={{ width:40, height:40, borderRadius:'50%', bgcolor: colors.bg, border:`1px solid ${colors.border}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {shop.image ? <Box component="img" src={shop.image} sx={{ width:'100%', height:'100%', borderRadius:'50%', objectFit:'cover' }} /> : <Store sx={{ fontSize:20, color: colors.primary }} />}
                </Box>
                <Box sx={{ flex:1, minWidth:0 }}>
                    <Box sx={{ display:'flex', alignItems:'center', gap:1, flexWrap:'wrap' }}>
                        <Typography sx={{ fontFamily: typography.poppins, fontSize:15, fontWeight:700, color: colors.textPrimary }} noWrap>{shop.shopName ?? 'Unnamed Shop'}</Typography>
                        <Box sx={{ px:0.8, py:0.1, bgcolor: planInfo?.bgColor ?? colors.bg, border:`1px solid ${planInfo?.borderColor ?? colors.border}`, borderRadius:1, flexShrink:0 }}>
                            <Typography sx={{ fontSize:9, color: planInfo?.color ?? colors.textFaintest, fontWeight:700 }}>{planInfo?.label?.toUpperCase() ?? 'FREE'}</Typography>
                        </Box>
                        <Box sx={{ px:0.8, py:0.1, bgcolor: pi.bg, border:`1px solid ${pi.border}`, borderRadius:1, flexShrink:0 }}>
                            <Typography sx={{ fontSize:9, color: pi.color, fontWeight:700 }}>{pi.label}</Typography>
                        </Box>
                    </Box>
                    <Typography sx={{ fontSize:11, color: colors.textFaint }} noWrap>{shop.address ?? '—'}</Typography>
                </Box>
                <Box sx={{ px:1.2, py:0.4, borderRadius:1, bgcolor: ss.bg, border:`1px solid ${ss.border}`, flexShrink:0 }}>
                    <Typography sx={{ fontSize:10, color: ss.color, fontWeight:700 }}>{ss.label}</Typography>
                </Box>
                <Typography sx={{ fontSize:14, color: colors.textFaintest }}>{expanded ? '▲' : '▼'}</Typography>
            </Box>

            {/* Expanded */}
            {expanded && (
                <Box sx={{ borderTop:`1px solid ${colors.border}` }}>

                    <Box sx={{ px:2, py:1.5, display:'flex', flexDirection:'column', gap:0.8 }}>
                        {[
                            { icon:'👤', label:'Owner',   value: shop.barberName ?? shop.ownerName ?? '—' },
                            { icon:'📧', label:'Email',   value: shop.email    ?? '—' },
                            { icon:'📞', label:'Phone',   value: shop.phone    ?? '—' },
                            { icon:'🕐', label:'Joined',  value: timeAgo(shop.createdAt) },
                            { icon:'🆔', label:'Shop ID', value: shop.id },
                        ].map(row => (
                            <Box key={row.label} sx={{ display:'flex', gap:1, alignItems:'flex-start' }}>
                                <Typography sx={{ fontSize:12 }}>{row.icon}</Typography>
                                <Typography sx={{ fontSize:11, color: colors.textFaintest, width:52, flexShrink:0 }}>{row.label}</Typography>
                                <Typography sx={{ fontSize:11, color: colors.textMuted, fontFamily: row.label === 'Shop ID' ? 'monospace' : 'inherit', wordBreak:'break-all' }}>{row.value}</Typography>
                            </Box>
                        ))}
                    </Box>

                    <Divider sx={{ borderColor: colors.border }} />

                    <Box sx={{ px:2, py:1.5 }}>
                        <Typography sx={{ fontSize:11, color: colors.textFaintest, mb:0.5, textTransform:'uppercase', letterSpacing:0.5 }}>Package Plan</Typography>
                        <PlanSelector current={plan} onSelect={handlePlan} saving={saving} />
                    </Box>

                    <Divider sx={{ borderColor: colors.border }} />

                    {/* Payment section */}
                    <Box sx={{ px:2, py:1.5 }}>
                        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:1 }}>
                            <Typography sx={{ fontSize:11, color: colors.textFaintest, textTransform:'uppercase', letterSpacing:0.5 }}>Subscription Payment</Typography>
                            <Box sx={{ px:1.2, py:0.3, borderRadius:1, bgcolor: pi.bg, border:`1px solid ${pi.border}` }}>
                                <Typography sx={{ fontSize:10, fontWeight:700, color: pi.color }}>{pi.label}</Typography>
                            </Box>
                        </Box>

                        {plan === 'free' ? (
                            <Typography sx={{ fontSize:12, color: colors.textDisabled }}>Free plan — no payment required.</Typography>
                        ) : (
                            <>
                                {paidUntilLabel && (
                                    <Box sx={{ bgcolor: pi.bg, border:`1px solid ${pi.border}`, borderRadius: radius.md, px:1.5, py:1, mb:1.5 }}>
                                        <Typography sx={{ fontSize:11, color: pi.color, fontWeight:600 }}>
                                            {pi.status === 'paid' || pi.status === 'expiring' ? `✅ Paid until ${paidUntilLabel}` : `❌ Expired on ${paidUntilLabel}`}
                                        </Typography>
                                        {shop.lastPaidAt && (
                                            <Typography sx={{ fontSize:10, color: colors.textFaintest, mt:0.3 }}>
                                                Last payment: {timeAgo(shop.lastPaidAt)}{shop.lastPaidMonths ? ` · ${shop.lastPaidMonths} month${shop.lastPaidMonths > 1 ? 's' : ''}` : ''}
                                            </Typography>
                                        )}
                                    </Box>
                                )}

                                {!showPayModal ? (
                                    <Box sx={{ display:'flex', gap:1 }}>
                                        <Button fullWidth onClick={() => setShowPayModal(true)} sx={{ bgcolor: colors.successBg, border:`1px solid ${colors.successBorder}`, color: colors.success, fontWeight:700, textTransform:'none', borderRadius: radius.md, fontSize:12, '&:hover':{ bgcolor: colors.successBorder } }}>
                                            💰 Mark as Paid
                                        </Button>
                                        {pi.status !== 'unpaid' && (
                                            <Button onClick={handleMarkUnpaid} disabled={saving} sx={{ bgcolor: colors.bg, border:`1px solid ${colors.border}`, color: colors.textFaint, textTransform:'none', borderRadius: radius.md, fontSize:11, px:1.5, '&:hover':{ color: colors.error, borderColor: colors.errorBorder } }}>
                                                Clear
                                            </Button>
                                        )}
                                    </Box>
                                ) : (
                                    <Box sx={{ bgcolor: colors.successBg, border:`1px solid ${colors.successBorder}`, borderRadius: radius.md, p:1.5 }}>
                                        <Typography sx={{ fontSize:12, color: colors.success, fontWeight:700, mb:1 }}>How long did they pay for?</Typography>
                                        <Box sx={{ display:'flex', gap:1, mb:1.5, flexWrap:'wrap' }}>
                                            {DURATIONS.map(d => {
                                                const active = selectedMonths === d.months;
                                                const base   = (pi.daysLeft > 0 && pi.paidUntil) ? pi.paidUntil : new Date();
                                                const until  = addMonths(base, d.months).toLocaleDateString('en-MY', { day:'numeric', month:'short', year:'numeric' });
                                                return (
                                                    <Box key={d.months} onClick={() => setSelectedMonths(d.months)} sx={{ flex:1, minWidth:70, textAlign:'center', py:1, borderRadius: radius.md, cursor:'pointer', bgcolor: active ? colors.successBorder : colors.bg, border:`1px solid ${active ? colors.success : colors.border}`, transition:'all .15s' }}>
                                                        <Typography sx={{ fontSize:12, fontWeight:700, color: active ? colors.success : colors.textFaint }}>{d.label}</Typography>
                                                        <Typography sx={{ fontSize:9, color: active ? `${colors.success}88` : colors.textDisabled }}>until {until}</Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                        <Box sx={{ display:'flex', gap:1 }}>
                                            <Button fullWidth onClick={() => setShowPayModal(false)} sx={{ bgcolor: colors.bg, border:`1px solid ${colors.border}`, color: colors.textFaint, textTransform:'none', borderRadius: radius.md, fontSize:12 }}>Cancel</Button>
                                            <Button fullWidth onClick={handleMarkPaid} disabled={saving} sx={{ bgcolor: colors.success, color: colors.bg, fontWeight:700, textTransform:'none', borderRadius: radius.md, fontSize:12, '&:hover':{ bgcolor:'${colors.success}' }, '&:disabled':{ bgcolor: colors.border, color: colors.textFaintest } }}>
                                                {saving ? <CircularProgress size={14} sx={{ color: colors.bgDeep }} /> : '✓ Confirm Payment'}
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                                <Typography sx={{ fontSize:10, color: colors.textDisabled, mt:0.8 }}>
                                    {planInfo?.price}/month · {pi.daysLeft > 0 ? 'Extending from current expiry' : 'Starting from today'}
                                </Typography>
                            </>
                        )}
                    </Box>

                    <Divider sx={{ borderColor: colors.border }} />

                    {/* Approve / Reject */}
                    <Box sx={{ px:2, pt:1.5, pb:1, display:'flex', gap:1 }}>
                        <Button fullWidth onClick={() => handleStatus('approved')} disabled={saving || status === 'approved'}
                            startIcon={saving ? <CircularProgress size={14} sx={{ color: colors.textMuted }} /> : <CheckCircle sx={{ fontSize:16 }} />}
                            sx={{ bgcolor: colors.successBg, border:`1px solid ${colors.successBorder}`, color: colors.success, fontWeight:700, textTransform:'none', borderRadius: radius.md, fontSize:13, '&:hover':{ bgcolor: colors.successBorder }, '&:disabled':{ bgcolor: colors.successBg, color:`${colors.success}88` } }}>
                            {status === 'approved' ? 'Approved ✓' : 'Approve'}
                        </Button>
                        <Button fullWidth onClick={() => handleStatus('rejected')} disabled={saving || status === 'rejected'}
                            startIcon={<Cancel sx={{ fontSize:16 }} />}
                            sx={{ bgcolor: colors.errorBg, border:`1px solid ${colors.errorBorder}`, color: colors.error, fontWeight:700, textTransform:'none', borderRadius: radius.md, fontSize:13, '&:hover':{ bgcolor: colors.errorBorder }, '&:disabled':{ bgcolor: colors.errorBg, color:`${colors.error}88` } }}>
                            {status === 'rejected' ? 'Rejected' : 'Reject'}
                        </Button>
                    </Box>

                    {/* Delete */}
                    <Box sx={{ px:2, pb:1.5 }}>
                        {!confirmDelete ? (
                            <Button fullWidth onClick={() => setConfirmDelete(true)} sx={{ bgcolor:'transparent', border:`1px dashed ${colors.errorBorder}`, color:`${colors.error}66`, fontWeight:600, textTransform:'none', borderRadius: radius.md, fontSize:12, '&:hover':{ bgcolor: colors.errorBg, color: colors.error, borderStyle:'solid' }, transition:'all .2s' }}>
                                🗑 Delete Shop
                            </Button>
                        ) : (
                            <Box sx={{ bgcolor: colors.errorBg, border:`1px solid ${colors.errorBorder}`, borderRadius: radius.md, p:1.5 }}>
                                <Typography sx={{ fontSize:12, color: colors.error, fontWeight:700, mb:0.5 }}>⚠️ Are you sure?</Typography>
                                <Typography sx={{ fontSize:11, color: colors.textMuted, mb:1.5 }}>
                                    This will permanently delete <Typography component="span" sx={{ color: colors.textPrimary, fontWeight:600 }}>{shop.shopName}</Typography> from Firestore. This cannot be undone.
                                </Typography>
                                <Box sx={{ display:'flex', gap:1 }}>
                                    <Button fullWidth onClick={() => setConfirmDelete(false)} sx={{ bgcolor: colors.bg, border:`1px solid ${colors.border}`, color: colors.textMuted, textTransform:'none', borderRadius: radius.md, fontSize:12 }}>Cancel</Button>
                                    <Button fullWidth onClick={handleDelete} disabled={deleting} sx={{ bgcolor:'${colors.errorBorder}', border:'1px solid ${colors.errorBorder}', color:colors.white, fontWeight:700, textTransform:'none', borderRadius: radius.md, fontSize:12, '&:hover':{ bgcolor:'${colors.errorBorder}' }, '&:disabled':{ bgcolor:colors.errorDeep, color: colors.textFaintest } }}>
                                        {deleting ? <CircularProgress size={14} sx={{ color: colors.textMuted }} /> : 'Yes, Delete'}
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const AdminPage = () => {
    const navigate = useNavigate();
    const [authChecked, setAuthChecked] = useState(false);
    const [isAdmin,     setIsAdmin]     = useState(false);
    const [shops,       setShops]       = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [filter,      setFilter]      = useState('all');

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (user) => { setIsAdmin(user?.uid === ADMIN_UID); setAuthChecked(true); });
        return () => unsub();
    }, []);

    useEffect(() => {
        if (!isAdmin) return;
        const load = async () => {
            setLoading(true);
            try {
                const snap = await getDocs(collection(db, 'shops'));
                const data = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => {
                    const aP = (a.status ?? 'pending') === 'pending' ? 0 : 1;
                    const bP = (b.status ?? 'pending') === 'pending' ? 0 : 1;
                    if (aP !== bP) return aP - bP;
                    return (b.createdAt?.toDate?.()?.getTime() ?? 0) - (a.createdAt?.toDate?.()?.getTime() ?? 0);
                });
                setShops(data);
            } catch (e) { console.error(e); }
            setLoading(false);
        };
        load();
    }, [isAdmin]);

    const handleUpdate = async (id, updates) => { await updateDoc(doc(db, 'shops', id), updates); setShops(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s)); };
    const handleDelete = async (id)          => { await deleteDoc(doc(db, 'shops', id)); setShops(prev => prev.filter(s => s.id !== id)); };

    if (!authChecked) return <Box sx={{ minHeight:'100vh', bgcolor: colors.bg, display:'flex', alignItems:'center', justifyContent:'center' }}><CircularProgress sx={{ color: colors.primary }} /></Box>;

    if (!isAdmin) return (
        <Box sx={{ minHeight:'100vh', bgcolor: colors.bg, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', px:3 }}>
            <Typography sx={{ fontSize:48, mb:2 }}>🚫</Typography>
            <Typography sx={{ fontFamily: typography.poppins, fontSize:20, fontWeight:700, color: colors.textPrimary, mb:1 }}>Access Denied</Typography>
            <Typography sx={{ fontSize:13, color: colors.textFaint, textAlign:'center', mb:3 }}>This page is restricted to administrators only.</Typography>
            <Button onClick={() => navigate('/')} sx={{ color: colors.primary, textTransform:'none' }}>← Go Home</Button>
        </Box>
    );

    const filtered = shops.filter(s => filter === 'all' || (s.status ?? (s.isActive ? 'approved' : 'pending')) === filter);
    const counts   = { all: shops.length, pending: shops.filter(s => (s.status ?? 'pending') === 'pending').length, approved: shops.filter(s => s.status === 'approved').length, rejected: shops.filter(s => s.status === 'rejected').length };
    const paymentCounts = { paid: shops.filter(s => getPaymentInfo(s).status === 'paid').length, expiring: shops.filter(s => getPaymentInfo(s).status === 'expiring').length, overdue: shops.filter(s => getPaymentInfo(s).status === 'overdue').length, unpaid: shops.filter(s => getPaymentInfo(s).status === 'unpaid').length };
    const planCounts = PLANS.reduce((acc, p) => { acc[p.key] = shops.filter(s => (s.plan ?? 'free') === p.key).length; return acc; }, {});

    return (
        <Box sx={{ minHeight:'100vh', bgcolor: colors.bg, color: colors.textPrimary, pb:8 }}>

            <Box sx={{ position:'sticky', top:0, zIndex:100, bgcolor: colors.bgTopBar, borderBottom:`1px solid ${colors.borderLight}`, px:3, py:2 }}>
                <Typography sx={{ fontFamily: typography.poppins, fontSize:20, fontWeight:700, color: colors.primary }}>🛠 DigitalQ Admin</Typography>
                <Typography sx={{ fontSize:11, color: colors.textFaintest }}>Logged in as admin</Typography>
            </Box>

            <Box sx={{ px:3, mt:3 }}>

                {/* Summary */}
                <Box sx={{ display:'flex', gap:1.5, mb:3, flexWrap:'wrap' }}>
                    {[
                        { label:'Total Shops', value: counts.all,      color: colors.primary },
                        { label:'Pending',     value: counts.pending,  color: colors.primary, highlight: counts.pending > 0 },
                        { label:'Approved',    value: counts.approved, color: colors.success },
                        { label:'Rejected',    value: counts.rejected, color: colors.error   },
                    ].map(s => (
                        <Box key={s.label} sx={{ flex:1, minWidth:70, bgcolor: s.highlight ? colors.warningBg : colors.bgCard, border:`1px solid ${s.highlight ? colors.primaryBorder : colors.border}`, borderRadius: radius.md, p:1.5, textAlign:'center' }}>
                            <Typography sx={{ fontFamily: typography.poppins, fontSize:26, fontWeight:700, color: s.color, lineHeight:1 }}>{s.value}</Typography>
                            <Typography sx={{ fontSize:10, color: colors.textFaintest, mt:0.3 }}>{s.label}</Typography>
                        </Box>
                    ))}
                </Box>

                {/* Plan breakdown */}
                <Box sx={{ bgcolor: colors.bgCard, border:`1px solid ${colors.border}`, borderRadius: radius.md, p:2, mb:2 }}>
                    <Typography sx={{ fontSize:11, color: colors.textFaintest, mb:1.5, textTransform:'uppercase', letterSpacing:0.5 }}>Plan Breakdown</Typography>
                    <Box sx={{ display:'flex', gap:1, flexWrap:'wrap' }}>
                        {PLANS.map(plan => (
                            <Box key={plan.key} sx={{ flex:1, minWidth:60, bgcolor: plan.bgColor, border:`1px solid ${plan.borderColor}`, borderRadius: radius.md, p:1, textAlign:'center' }}>
                                <Typography sx={{ fontFamily: typography.poppins, fontSize:20, fontWeight:700, color: plan.color, lineHeight:1 }}>{planCounts[plan.key] ?? 0}</Typography>
                                <Typography sx={{ fontSize:10, color: colors.textFaintest, mt:0.2 }}>{plan.label}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Payment health */}
                <Box sx={{ bgcolor: colors.bgCard, border:`1px solid ${colors.border}`, borderRadius: radius.md, p:2, mb:3 }}>
                    <Typography sx={{ fontSize:11, color: colors.textFaintest, mb:1.5, textTransform:'uppercase', letterSpacing:0.5 }}>Payment Health</Typography>
                    <Box sx={{ display:'flex', gap:1, flexWrap:'wrap' }}>
                        {[
                            { label:'💰 Paid',     value: paymentCounts.paid,     color: colors.success, bg: colors.successBg, border: colors.successBorder },
                            { label:'⚠️ Expiring', value: paymentCounts.expiring, color: colors.warning, bg: colors.warningBg, border: colors.warningBorder, highlight: paymentCounts.expiring > 0 },
                            { label:'🚨 Overdue',  value: paymentCounts.overdue,  color: colors.error,   bg: colors.errorBg,   border: colors.errorBorder,   highlight: paymentCounts.overdue > 0  },
                            { label:'⏳ Unpaid',   value: paymentCounts.unpaid,   color: colors.primary, bg: colors.warningBg, border: colors.warningBorder },
                        ].map(s => (
                            <Box key={s.label} sx={{ flex:1, minWidth:60, bgcolor: s.bg, border:`1px solid ${s.highlight ? s.border : colors.border}`, borderRadius: radius.md, p:1, textAlign:'center' }}>
                                <Typography sx={{ fontFamily: typography.poppins, fontSize:20, fontWeight:700, color: s.color, lineHeight:1 }}>{s.value}</Typography>
                                <Typography sx={{ fontSize:9, color: colors.textFaintest, mt:0.2 }}>{s.label}</Typography>
                            </Box>
                        ))}
                    </Box>
                    {(paymentCounts.expiring > 0 || paymentCounts.overdue > 0) && (
                        <Typography sx={{ fontSize:11, color: colors.error, mt:1 }}>⚠️ {paymentCounts.overdue} overdue · {paymentCounts.expiring} expiring within 7 days</Typography>
                    )}
                </Box>

                {/* Filter tabs */}
                <Box sx={{ display:'flex', gap:1, mb:2.5, overflowX:'auto', pb:0.5, scrollbarWidth:'none', '&::-webkit-scrollbar':{ display:'none' } }}>
                    {['all','pending','approved','rejected'].map(f => (
                        <Box key={f} onClick={() => setFilter(f)} sx={{ flexShrink:0, px:1.5, py:0.8, borderRadius: radius.md, cursor:'pointer', bgcolor: filter === f ? colors.primary : colors.bgCard, border:`1px solid ${filter === f ? colors.primary : colors.border}`, transition:'all .15s' }}>
                            <Typography sx={{ fontSize:12, fontWeight:600, color: filter === f ? colors.bg : colors.textMuted, textTransform:'capitalize' }}>{f} ({counts[f]})</Typography>
                        </Box>
                    ))}
                </Box>

                {/* List */}
                {loading ? (
                    <Box sx={{ display:'flex', justifyContent:'center', py:6 }}><CircularProgress sx={{ color: colors.primary }} /></Box>
                ) : filtered.length === 0 ? (
                    <Box sx={{ textAlign:'center', py:6 }}>
                        <Typography sx={{ fontSize:28, mb:1 }}>🏪</Typography>
                        <Typography sx={{ fontSize:14, color: colors.textFaint }}>No shops in this category</Typography>
                    </Box>
                ) : filtered.map(shop => <ShopCard key={shop.id} shop={shop} onUpdate={handleUpdate} onDelete={handleDelete} />)}
            </Box>
        </Box>
    );
};

export default AdminPage;